import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, useLocation } from 'react-router-dom';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StorageIcon from '@mui/icons-material/Storage';
import LinkIcon from '@mui/icons-material/Link';
import BugReportIcon from '@mui/icons-material/BugReport';
import MemoryIcon from '@mui/icons-material/Memory';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RadarIcon from '@mui/icons-material/Radar';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HubIcon from '@mui/icons-material/Hub';


// NEW: Threat model icon
import AccountTreeIcon from '@mui/icons-material/AccountTree';

function Nav() {
  const navigate = useNavigate();
  const location = useLocation();

  // extract pentest_id as the last segment
  const pathParts = location.pathname.replace(/\/$/, '').split('/').filter(Boolean);
  const pentest_id = pathParts[pathParts.length - 1];

  /**
   * Tab indexes:
   * 0 = Dashboard
   * 1 = Scope
   * 2 = Recon (dropdown)
   * 3 = Findings (dropdown)
   * 4 = Threat Model (direct nav)
   * 5 = Report (dropdown)
   */
  const [selectedTab, setSelectedTab] = useState(0);
  const [reconAnchorEl, setReconAnchorEl] = useState(null);
  const [findingsAnchorEl, setFindingsAnchorEl] = useState(null);
  const [reportAnchorEl, setReportAnchorEl] = useState(null);

  // Update selected tab based on URL
  useEffect(() => {
    const path = location.pathname;

    if (/^\/pentest\/dashboard\/\d+$/.test(path)) {
      setSelectedTab(0);
    } else if (/^\/pentest\/scope\/\d+$/.test(path)) {
      setSelectedTab(1);
    } else if (/^\/pentest\/(assets|urls|attack-graph)\/\d+$/.test(path)) {
      setSelectedTab(2);
    } else if (/^\/pentest\/(findings|cves)\/\d+$/.test(path)) {
      setSelectedTab(4);
    } else if (/^\/pentest\/threat_model\/\d+$/.test(path)) {
      setSelectedTab(3);
    } else if (/^\/pentest\/report\/(pentest|executive|attestation|recon)\/\d+$/.test(path)) {
      setSelectedTab(5);
    } 
    else if (/^\/pentest\/attack_history\/\d+$/.test(path)) {
      setSelectedTab(6);
    }else {
      setSelectedTab(0);
    }
  }, [location.pathname]);

  // Tab change handler (skip dropdown tabs: Recon=2, Findings=3, Report=5)
  const handleTabChange = (event, newValue) => {
    if (newValue === 2 || newValue === 4 || newValue === 5) return;

    setSelectedTab(newValue);

    switch (newValue) {
      case 0:
        navigate(`/pentest/dashboard/${pentest_id}`);
        break;
      case 1:
        navigate(`/pentest/scope/${pentest_id}`);
        break;
      case 3:
        navigate(`/pentest/threat_model/${pentest_id}`);
        break;
      case 6:
        navigate(`/pentest/attack_history/${pentest_id}`);
        break;
      default:
        break;
    }
  };

  // Recon dropdown handlers
  const handleReconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setReconAnchorEl(e.currentTarget);
  };
  const handleReconClose = () => setReconAnchorEl(null);
  const handleReconItem = (route) => {
    setReconAnchorEl(null);
    setSelectedTab(2);
    navigate(`/pentest/${route}/${pentest_id}`);
  };

  // Findings dropdown handlers
  const handleFindingsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFindingsAnchorEl(e.currentTarget);
  };
  const handleFindingsClose = () => setFindingsAnchorEl(null);
  const handleFindingsItem = (route) => {
    setFindingsAnchorEl(null);
    setSelectedTab(4);
    navigate(`/pentest/${route}/${pentest_id}`);
  };

  // Reports dropdown handlers
  const handleReportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setReportAnchorEl(e.currentTarget);
  };
  const handleReportClose = () => setReportAnchorEl(null);
  const handleReportItem = (type) => {
    setReportAnchorEl(null);
    setSelectedTab(5);
    navigate(`/pentest/report/${type}/${pentest_id}`);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        padding: '10px',
        borderRadius: '10px',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
        marginBottom: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Pentest
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Navigation Tabs"
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab icon={<DashboardIcon />} label="Dashboard" />
        <Tab icon={<AssignmentIcon />} label="Scope" />

        {/* Recon dropdown */}
        <Tab
          icon={<RadarIcon />}
          label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Recon <ArrowDropDownIcon fontSize="small" />
            </div>
          }
          onClick={handleReconClick}
        />

        {/* ✅ NEW: Threat Model as its own nav tab */}
        <Tab icon={<AccountTreeIcon />} label="Threat Model" />

        {/* Findings dropdown */}
        <Tab
          icon={<BugReportIcon />}
          label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Findings <ArrowDropDownIcon fontSize="small" />
            </div>
          }
          onClick={handleFindingsClick}
        />

        {/* Report dropdown */}
        <Tab
          icon={<DescriptionIcon />}
          label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Report <ArrowDropDownIcon fontSize="small" />
            </div>
          }
          onClick={handleReportClick}
        />
        <Tab icon={<MemoryIcon />} label="Logs" />
      </Tabs>

      {/* Recon Menu */}
      <Menu anchorEl={reconAnchorEl} open={Boolean(reconAnchorEl)} onClose={handleReconClose}>
        <MenuItem onClick={() => handleReconItem('assets')}>
          <StorageIcon style={{ marginRight: 8 }} /> Assets
        </MenuItem>
        <MenuItem onClick={() => handleReconItem('urls')}>
          <LinkIcon style={{ marginRight: 8 }} /> Urls
        </MenuItem>
        <MenuItem onClick={() => handleReconItem('attack-graph')}>
          <HubIcon style={{ marginRight: 8 }} /> Graph View
        </MenuItem>
      </Menu>

      {/* Findings Menu */}
      <Menu anchorEl={findingsAnchorEl} open={Boolean(findingsAnchorEl)} onClose={handleFindingsClose}>
        <MenuItem onClick={() => handleFindingsItem('findings')}>
          <BugReportIcon style={{ marginRight: 8 }} /> Pentest Findings
        </MenuItem>
        <MenuItem onClick={() => handleFindingsItem('cves')}>
          <IntegrationInstructionsIcon style={{ marginRight: 8 }} /> CVE Scanner
        </MenuItem>
      </Menu>

      {/* Reports Menu */}
      <Menu anchorEl={reportAnchorEl} open={Boolean(reportAnchorEl)} onClose={handleReportClose}>
        <MenuItem onClick={() => handleReportItem('pentest')}>
          <DescriptionIcon style={{ marginRight: 8 }} /> Pentest Report
        </MenuItem>
        <MenuItem onClick={() => handleReportItem('executive')}>
          <AssessmentIcon style={{ marginRight: 8 }} /> Executive Report
        </MenuItem>
        <MenuItem onClick={() => handleReportItem('recon')}>
          <AssessmentIcon style={{ marginRight: 8 }} /> Recon Report
        </MenuItem>
        <MenuItem onClick={() => handleReportItem('attestation')}>
          <EmojiEventsIcon style={{ marginRight: 8 }} /> Attestation
        </MenuItem>

      </Menu>

       
         
    </Box>
  );
}

export default Nav;
